import { UserRoles } from '@sparkai/shared-types'
import { AppsPaths, getAppPort, getBaseShareLinkTargetApp, type ShareLinkTargetApp } from '@sparkai/utils'

export type ShareLinkResolutionResult =
  | { outcome: 'allowed'; destinationUrl: string }
  | { outcome: 'unauthenticated' }
  | { outcome: 'no-access'; message: string; nextStep: 'request-access-contact-admin' }
  | { outcome: 'unsupported-role'; message: string; nextStep: 'request-access-contact-admin' }
  | { outcome: 'not-found'; message: string }

export const NO_ACCESS_RESULT = {
  outcome: 'no-access',
  message: 'You do not have access to this shared link. Request access or contact your admin.',
  nextStep: 'request-access-contact-admin',
} as const

export const UNSUPPORTED_ROLE_RESULT = {
  outcome: 'unsupported-role',
  message: 'This shared link is not available for your role. Request access or contact your admin.',
  nextStep: 'request-access-contact-admin',
} as const

export const ENGAGEMENT_NOT_FOUND_RESULT = {
  outcome: 'not-found',
  message: 'We could not find this engagement.',
} as const

export const TASK_NOT_FOUND_RESULT = {
  outcome: 'not-found',
  message: 'We could not find this task.',
} as const

type ShareLinkAccessStatus = 'no-access' | 'engagement-not-available' | 'task-not-available'

export function getShareLinkTargetApp(role?: UserRoles): ShareLinkTargetApp {
  return getBaseShareLinkTargetApp(role)
}

export function canOpenAdminShareLink(role?: UserRoles) {
  return getShareLinkTargetApp(role) === 'admin'
}

export function canOpenCustomerShareLink(role?: UserRoles) {
  return getShareLinkTargetApp(role) === 'customer'
}

export function canOpenMissionSpecialistShareLink(role?: UserRoles) {
  return getShareLinkTargetApp(role) === 'mission-specialist'
}

export function canOpenWorkforceManagerShareLink(role?: UserRoles) {
  return getShareLinkTargetApp(role) === 'workforce-manager'
}

export function buildAppUrl(appPath: AppsPaths, path: string) {
  const normalizedPath = path.replace(/^\/+/, '')

  if (import.meta.env.DEV && globalThis.location.hostname !== 'spark.local') {
    return `${globalThis.location.protocol}//${globalThis.location.hostname}:${getAppPort(appPath)}${appPath}${normalizedPath}`
  }

  return `${globalThis.location.origin}${appPath}${normalizedPath}`
}

const shareLinkStatusKeyByAppPath: Partial<Record<AppsPaths, string>> = {
  '/admin/': 'sparkai:share-link-status:admin',
  '/customer/': 'sparkai:share-link-status:customer',
  '/ms/': 'sparkai:share-link-status:ms',
}

export function buildNoAccessDestination(
  appPath: AppsPaths,
  status: ShareLinkAccessStatus,
  path = 'no-access'
) {
  const statusKey = shareLinkStatusKeyByAppPath[appPath]

  try {
    if (statusKey) globalThis.sessionStorage?.setItem(statusKey, status)
  } catch {
    void 0
  }

  return buildAppUrl(appPath, path)
}

export function buildAdminTaskDestination(engagementId: string, taskId: string) {
  const filter = `(key:tab,value:tasks),(key:taskId,value:${taskId})`
  return buildAppUrl('/admin/', `engagement/${engagementId}?filters=${btoa(filter)}`)
}

export function buildWfmTaskDestination(taskId: string) {
  return buildAppUrl('/admin/', `task/${taskId}`)
}
